import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux';
import { selectGeneralOptions, selectIsMobileRoute, selectUtmMinimum } from '../../features/mobile/mobileSlice';
import { selectCartItems, selectCartZipcode, selectOrderInProgress, setCartZipcode } from '../../features/cart/cartSlice';
import CartObj from '../../features/cart/cartObj';
import { useNavigate } from 'react-router-dom';
import { selectShippingMethods } from '../../features/shipping/shippingSlice';
import ShippingMethodObj from '../../features/shipping/shippingMethodObj';
import { FormValues } from './interfaces';
import { useFormikContext } from 'formik';

interface Props {
    showAddressVerification: boolean;
    isAddressVerified: boolean;
    setShowAddressVerification: (showAddressVerification: boolean) => void;
    setIsAddressVerified: (isAddressVerified: boolean) => void;
    addressResult: any;
}

export default function ModalPopups({ showAddressVerification, isAddressVerified, setShowAddressVerification, setIsAddressVerified, addressResult }: Props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const formik = useFormikContext<FormValues>();
    const [regionalShipLimit, setRegionalShipLimit] = useState(0);
    const orderInProgress = useSelector(selectOrderInProgress);
    const AppURL = useSelector(selectIsMobileRoute);
    const cartItems = useSelector(selectCartItems);
    const shippingMethods = useSelector(selectShippingMethods);
    const cartZipcode = useSelector(selectCartZipcode);
    const utmMinimum = useSelector(selectUtmMinimum);
    const getGeneralOptions = useSelector(selectGeneralOptions);
    const cart = useMemo(() => {
        return new CartObj(cartItems);
    }, [cartItems]);
    const QURLParams = AppURL ? `/?${AppURL}` : '/';
    const shippingMethod = ShippingMethodObj.getById(
        shippingMethods,
        Number(formik.values.shipping_method)
    );
    const [addressChange, setAddressChange] = useState('new-address');
    const isNotEmailDelivery = !ShippingMethodObj.isEmailDelivery(formik.values.shipping_method);
    const oldStreet = (formik?.values && Boolean(formik.values.use_shipping) && isNotEmailDelivery) ?
        formik.values.shipping_address_1 : formik.values.billing_address_1;
    const oldCity = (formik?.values && Boolean(formik.values.use_shipping) && isNotEmailDelivery) ?
        formik.values.shipping_city : formik.values.billing_city;
    const oldState = (formik?.values && Boolean(formik.values.use_shipping) && isNotEmailDelivery) ?
        formik.values.shipping_state : formik.values.billing_state;
    const oldZipCode = (formik?.values && Boolean(formik.values.use_shipping) && isNotEmailDelivery) ?
        formik.values.shipping_postcode : formik.values.billing_postcode;

    const capitalizeWords = (str: string) => {
        return str
            .toLowerCase()
            .split(" ")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    const newResult = addressResult && addressResult.uspsData && addressResult.uspsData.standardizedAddress;
    const newStreet = newResult && newResult.firstAddressLine ? capitalizeWords(newResult.firstAddressLine) : "";
    const newCity = newResult && newResult.city ? capitalizeWords(newResult.city) : "";
    const newState = newResult && newResult.state;
    const newZipCode = newResult && newResult.zipCode;

    useEffect(() => {
        const isAddressSame = ((newStreet === oldStreet) && (newCity === oldCity) &&
            (newState === oldState) && (newZipCode === oldZipCode));
        if (!showAddressVerification && isAddressVerified && !isAddressSame) {
            setIsAddressVerified(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.billing_address_1, formik.values.billing_city, formik.values.billing_state, formik.values.billing_postcode,
    formik.values.shipping_address_1, formik.values.shipping_city, formik.values.shipping_state, formik.values.shipping_postcode
    ]);

    useEffect(() => {
        async function fetchData() {
            try {
                if (shippingMethod && shippingMethod.data.ID) {
                    if (getGeneralOptions) {
                        const { regional_shipping_meal_limit } = getGeneralOptions;

                        const isUPSOrUPSFreeShipping = shippingMethod?.data.ups_delivery_method === true;

                        const isRegionalMinOverrides = isUPSOrUPSFreeShipping && utmMinimum && utmMinimum?.minimum_value;
                        const isRegionalMealLimit = isUPSOrUPSFreeShipping && regional_shipping_meal_limit;

                        const shipLimit = isRegionalMinOverrides ? Number(utmMinimum?.minimum_value)
                            : (isRegionalMealLimit ? Number(regional_shipping_meal_limit) : 0);

                        setRegionalShipLimit(shipLimit);
                    }
                }
            } catch (error) {
                console.error(error);
            }
        }
        fetchData();
    }, [shippingMethods, cartZipcode, getGeneralOptions, utmMinimum, shippingMethod]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setAddressChange(e.target.id);
    }

    const handleAddressVerification = async () => {
        if (addressChange === 'new-address') {
            const isNotEmailDelivery = !ShippingMethodObj.isEmailDelivery(formik?.values.shipping_method);
            const address1 = (Boolean(formik?.values?.use_shipping) && isNotEmailDelivery) ? 'shipping_address_1' : 'billing_address_1';
            const city = (Boolean(formik?.values?.use_shipping) && isNotEmailDelivery) ? 'shipping_city' : 'billing_city';
            const state = (Boolean(formik?.values?.use_shipping) && isNotEmailDelivery) ? 'shipping_state' : 'billing_state';
            const postCode = (Boolean(formik?.values?.use_shipping) && isNotEmailDelivery) ? 'shipping_postcode' : 'billing_postcode';

            formik.setFieldValue(address1, newStreet);
            formik.setFieldValue(city, newCity);
            formik.setFieldValue(state, newState);
            formik.setFieldValue(postCode, newZipCode);
            await dispatch(setCartZipcode(newZipCode));
        }
        setIsAddressVerified(true);
        setShowAddressVerification(false);
    }

    return (
        <React.Fragment>
            {(regionalShipLimit > 0 && regionalShipLimit > cart.getItemCount() && !orderInProgress) &&
                <Modal show={true} backdrop="static">
                    <Modal.Body className='font-barlow mb-4'>
                        <div className='text-center fs-4'>
                            <p className='mt-2'>Shipping requires a minimum of {regionalShipLimit} meals.
                                Please add {regionalShipLimit} meals to your cart to continue to checkout</p>
                        </div>
                        <div className='d-flex justify-content-center p-3'>
                            <Button variant="dark" onClick={() => { navigate(`/order${QURLParams}`) }}>Add Items To Cart</Button>
                        </div>
                    </Modal.Body>
                </Modal>}
            <Modal style={{ top: '20%' }} show={showAddressVerification} backdrop="static" onHide={() => setShowAddressVerification(false)} keyboard={false}>
                <Modal.Header className='border-bottom-0' closeButton>
                    <div className='text-center d-flex justify-content-center w-100 fs-4'>
                        <p className='mt-2'>Address verification</p>
                    </div>
                </Modal.Header>
                <Modal.Body className='font-barlow'>
                    <div className='text-center'>
                        <p className='mb-0'>The address you entered appears to be incorrect or incomplete.</p>
                        <p>Please verify your address to ensure a timely delivery.</p>
                    </div>
                    <Form className='fs-6 mx-2 text-center d-flex flex-row justify-content-between'>
                        <div className="border p-3 w-50 text-start">
                            <Form.Check
                                type="radio"
                                name="address-field"
                                label="Use As Entered"
                                id="old-address"
                                checked={addressChange === "old-address"}
                                onChange={handleChange}
                            />
                            <p className='mt-2'>{oldStreet}, {oldCity}, {oldState}, {oldZipCode}</p>
                        </div>
                        <div className="border p-3 w-50 ms-3 text-start">
                            <Form.Check
                                type="radio"
                                name="address-field"
                                label="Verified Address"
                                id="new-address"
                                checked={addressChange === "new-address"}
                                onChange={handleChange}
                            />
                            <p className='mt-2'>{newStreet}, {newCity}, {newState}, {newZipCode}</p>
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer className='border-top-0'>
                    <div className='d-flex justify-content-end'>
                        <Button variant="dark" className='mx-2' onClick={() => setShowAddressVerification(false)}>
                            Cancel
                        </Button>
                        <Button variant="success" className='text-white' onClick={handleAddressVerification}>
                            Submit
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </React.Fragment >
    )
}
